// Fetch a Tina pipeline event's stored GCS artifact bytes (bearer-authed)
// through the orchestrator, so the browser can render the stored image via a
// blob: object URL instead of hotlinking the candidate's original source URL.
// Hotlinking breaks for hosts that block cross-origin embedding — e.g.
// Instagram serves `Cross-Origin-Resource-Policy: same-origin`, so an
// <img src="https://scontent-*.cdninstagram.com/..."> is refused by the
// browser even though the bytes exist. The orchestrator route is scoped to the
// event's own artifact namespace. See tina src/orchestrator/routes/events.py.
// The bearer is the curator's Firebase ID token (lib/tina/auth), which is also
// why this cannot be a plain <img src>: an <img> cannot send a header.
import { tinaAuthHeaders } from "@/lib/tina/auth";

const ORCHESTRATOR_URL = import.meta.env.VITE_TINA_ORCHESTRATOR_URL ?? "";

/** Orchestrator URL that streams one event artifact's raw bytes. */
export function artifactBytesUrl(eventId: string, ref: string): string {
  return `${ORCHESTRATOR_URL}/events/${encodeURIComponent(
    eventId,
  )}/artifact?ref=${encodeURIComponent(ref)}`;
}

/** Fetch an event artifact's bytes as a Blob (throws on non-2xx). */
export async function fetchArtifactBlob(
  eventId: string,
  ref: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await fetch(artifactBytesUrl(eventId, ref), {
    headers: await tinaAuthHeaders(),
    signal,
  });
  if (!response.ok) {
    throw new Error(`Artifact fetch failed: ${response.status}`);
  }
  return response.blob();
}
